import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import { EditType } from './ViewSolutionDetails';
import CloseIcon from '@mui/icons-material/Close';

import TextareaAutosize from '@mui/material/TextareaAutosize';

interface SolutionEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  type: EditType;
  solutionData: any;
  setSolutionData: (data: any) => void;
  editItem?: any;
  setLlmResponseData: (data: any) => void;
}

const SolutionEditModal: React.FC<SolutionEditModalProps> = ({
  isOpen,
  onClose,
  type,
  solutionData,
  setSolutionData,
  onSave,
  editItem,
  setLlmResponseData,
}) => {
  // State for data_requirements editing
  const [editedName, setEditedName] = useState(editItem?.name || '');
  const [editedSourceCategory, setEditedSourceCategory] = useState<'internal' | 'external'>(
    editItem?.sourceCategory || 'internal'
  );
  const [editedDataStructure, setEditedDataStructure] = useState(editItem?.type || 'Structured');
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>(
    editItem?.technologies || []
  );
  // State for adding new key requirement
  const [newRequirement, setNewRequirement] = useState('');

  // Reset state when modal opens or type/editItem changes
  useEffect(() => {
    if (isOpen) {
      if (type === 'data_requirements' || type === 'add_data_requirements') {
        setEditedName(editItem?.name || '');
        setEditedSourceCategory(editItem?.sourceCategory || 'internal');
        setEditedDataStructure(editItem?.type || 'Structured');
        setSelectedTechnologies(editItem?.technologies || []);
      }
      if (type === 'add_key_requirements') {
        setNewRequirement('');
      }
    }
  }, [isOpen, type, editItem]);

  useEffect(() => {
    if (!isOpen) return;

    setEditedName(editItem.originalName);
    setEditedSourceCategory(editItem.sourceCategory);
    setEditedDataStructure(editItem.type);
    setSelectedTechnologies(editItem.technologies || []);
  }, [isOpen, editItem]);

  const handleUpdate = () => {
    onSave(solutionData);
    onClose();
  };
  if (type === 'overview') {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '500px',
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Edit Overview
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Description
            </Typography>
            <TextareaAutosize
              aria-label="minimum height"
              minRows={3}
              defaultValue={solutionData.description}
              onChange={(e) => setSolutionData({ ...solutionData, description: e.target.value })}
              style={{
                width: 'auto',
                fontFamily: 'Poppins',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                padding: '8px',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" size="small" color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'impact') {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '500px',
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Edit Impact
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Status
            </Typography>
            <Select
              labelId="roi-select-label"
              id="roi-select"
              value={solutionData.impact || ''}
              sx={{ zIndex: 1601 }}
              onChange={(e) => setSolutionData({ ...solutionData, impact: e.target.value })}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value={'Breakthrough'}>BREAKTHROUGH</MenuItem>
              <MenuItem value={'Transformative'}>TRANSFORMATIVE</MenuItem>
              <MenuItem value={'Incremental'}>INCREMENTAL</MenuItem>
            </Select>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" size="small" color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'name') {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '500px',
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Edit Name
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextareaAutosize
              aria-label="minimum height"
              minRows={3}
              defaultValue={solutionData.opportunity_name}
              onChange={(e) =>
                setSolutionData({ ...solutionData, opportunity_name: e.target.value })
              }
              style={{
                width: 'auto',
                fontFamily: 'Poppins',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                padding: '8px',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" size="small" color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'roi') {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '500px',
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Edit ROI
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Status
            </Typography>
            <Select
              labelId="roi-select-label"
              id="roi-select"
              value={solutionData.roi || ''}
              sx={{ zIndex: 1601 }}
              onChange={(e) => setSolutionData({ ...solutionData, roi: e.target.value })}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value={'HIGH'}>HIGH</MenuItem>
              <MenuItem value={'MEDIUM'}>MEDIUM</MenuItem>
              <MenuItem value={'LOW'}>LOW</MenuItem>
            </Select>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Description
            </Typography>
            <TextareaAutosize
              aria-label="minimum height"
              minRows={3}
              defaultValue={solutionData.roi_description}
              onChange={(e) =>
                setSolutionData({ ...solutionData, roi_description: e.target.value })
              }
              style={{
                width: 'auto',
                fontFamily: 'Poppins',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                padding: '8px',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" size="small" color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'complexity') {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '500px',
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Edit Complexity
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Status
            </Typography>
            <Select
              labelId="complexity-select-label"
              id="complexity-select"
              value={solutionData.complexity}
              sx={{ zIndex: 1601 }}
              onChange={(e) => setSolutionData({ ...solutionData, complexity: e.target.value })}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value={'HIGH'}>HIGH</MenuItem>
              <MenuItem value={'MEDIUM'}>MEDIUM</MenuItem>
              <MenuItem value={'LOW'}>LOW</MenuItem>
            </Select>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Description
            </Typography>
            <TextareaAutosize
              aria-label="minimum height"
              minRows={3}
              defaultValue={solutionData.complexity_description}
              onChange={(e) =>
                setSolutionData({ ...solutionData, complexity_description: e.target.value })
              }
              style={{
                width: 'auto',
                fontFamily: 'Poppins',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                padding: '8px',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" size="small" color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'key_requirements') {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const updatedKeyRequirements = [...solutionData.key_requirements];
      updatedKeyRequirements[editItem.index] = e.target.value;
      setSolutionData({ ...solutionData, key_requirements: updatedKeyRequirements });
    };

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '500px',
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Edit Key Requirements
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Name
            </Typography>
            <TextField
              variant="outlined"
              sx={{ width: 'auto' }}
              defaultValue={solutionData.key_requirements[editItem.index]}
              onChange={(e) => handleChange(e)}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" size="small" color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'data_requirements') {
    // Find matching ai_solution entry based on the current data asset name
    const matchingAiSolution = solutionData?.agent?.ai_solution?.find(
      (solution: any) => solution.data_asset.toLowerCase() === editItem.originalName.toLowerCase()
    );

    useEffect(() => {
      if (matchingAiSolution?.technology) {
        setSelectedTechnologies(matchingAiSolution.technology);
      }
    }, [matchingAiSolution]);

    useEffect(() => {
      if (!editItem?.originalName) return;

      const updatedAiSolution = solutionData?.agent?.ai_solution?.find(
        (solution: any) => solution.data_asset.toLowerCase() === editItem.originalName.toLowerCase()
      );

      if (updatedAiSolution) {
        setEditedName(updatedAiSolution.data_asset);
        setSelectedTechnologies(updatedAiSolution.technology || []);
      }
    }, [solutionData, editItem?.originalName]);

    // Get available technologies from the matching ai_solution
    const availableTechnologies = matchingAiSolution?.technology || [];

    const handleTechnologyChange = (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value;
      setSelectedTechnologies(typeof value === 'string' ? value.split(',') : value);
    };

    const handleDeleteDataRequirement = () => {
      // Remove the item from the appropriate array
      const sourceCategory = editItem.sourceCategory as 'internal' | 'external';
      const structureType = editItem.type.toLowerCase() as 'structured' | 'unstructured';

      const updatedDataSources = { ...solutionData.data_sources };
      updatedDataSources[sourceCategory][structureType] = updatedDataSources[sourceCategory][
        structureType
      ].filter((item: string) => item !== editItem.originalName);

      const updatedSolution = { ...solutionData, data_sources: updatedDataSources };
      if (setLlmResponseData) {
        setLlmResponseData((prev: any) =>
          prev?.filter(
            (asset: any) => asset.data_asset.toLowerCase() !== editItem.originalName.toLowerCase()
          )
        );
      }
      setSolutionData(updatedSolution);
      onSave(updatedSolution);
      onClose();
    };

    const handleUpdateDataRequirement = () => {
      // Remove from old location
      const oldSourceCategory = editItem.sourceCategory as 'internal' | 'external';
      const oldStructureType = editItem.type.toLowerCase() as 'structured' | 'unstructured';

      // Add to new location
      const newSourceCategory = editedSourceCategory;
      const newStructureType = editedDataStructure.toLowerCase() as 'structured' | 'unstructured';

      const updatedDataSources = { ...solutionData.data_sources };

      // Remove from old location
      updatedDataSources[oldSourceCategory][oldStructureType] = updatedDataSources[
        oldSourceCategory
      ][oldStructureType].filter((item: string) => item !== editItem.originalName);

      // Add to new location (avoid duplicates)
      if (!updatedDataSources[newSourceCategory][newStructureType].includes(editedName)) {
        updatedDataSources[newSourceCategory][newStructureType] = [
          ...updatedDataSources[newSourceCategory][newStructureType],
          editedName,
        ];
      }

      // Update the ai_solution entry with selected technologies if it exists
      // Disabling this code as it was causing issue in updating data sources
      // if (matchingAiSolution && selectedTechnologies.length > 0) {
      //   const updatedAiSolution = solutionData.agent?.ai_solution?.map((solution: any) => {
      //     if (solution.data_asset.toLowerCase() === editItem.originalName.toLowerCase()) {
      //       return {
      //         ...solution,
      //         data_asset: editedName,
      //         technology: selectedTechnologies,
      //       };
      //     }
      //     return solution;
      //   });

      //   const updatedSolution = {
      //     ...solutionData,
      //     data_sources: updatedDataSources,
      //     agent: {
      //       ...solutionData.agent,
      //       ai_solution: updatedAiSolution,
      //     },
      //   };

      //   if (setLlmResponseData) {
      //     setLlmResponseData((prev: any) => {
      //       if (!prev) return prev;

      //       return prev.map((asset: any) => {
      //         if (asset.data_asset.toLowerCase() === editItem.originalName.toLowerCase()) {
      //           return {
      //             ...asset,
      //             data_asset: editedName,
      //             technology: selectedTechnologies,
      //           };
      //         }
      //         return asset;
      //       });
      //     });
      //   }
      //   if (setUpdatedSolutionData) {
      //     setUpdatedSolutionData((prev: any) => {
      //       if (!prev) return prev;

      //       return {
      //         ...prev,
      //         data_sources: updatedDataSources,
      //         agent: {
      //           ...prev.agent,
      //           ai_solution: prev.agent?.ai_solution?.map((solution: any) => {
      //             if (solution.data_asset.toLowerCase() === editItem.originalName.toLowerCase()) {
      //               return {
      //                 ...solution,
      //                 data_asset: editedName,
      //                 technology: selectedTechnologies,
      //               };
      //             }
      //             return solution;
      //           }),
      //         },
      //       };
      //     });
      //   }

      //   setSolutionData(updatedSolution);
      //   onSave(updatedSolution);
      // } else {
      //   const updatedSolution = { ...solutionData, data_sources: updatedDataSources };
      //   setSolutionData(updatedSolution);
      //   onSave(updatedSolution);
      // }

      const updatedAiSolution = solutionData.agent?.ai_solution?.map((solution: any) => {
        if (solution.data_asset.toLowerCase() === editItem.originalName.toLowerCase()) {
          return {
            ...solution,
            data_asset: editedName,
            technology: selectedTechnologies, // ← empty array allowed
          };
        }
        return solution;
      });

      const updatedSolution = {
        ...solutionData,
        data_sources: updatedDataSources,
        agent: {
          ...solutionData.agent,
          ai_solution: updatedAiSolution,
        },
      };

      setSolutionData(updatedSolution);
      onSave(updatedSolution);
      setLlmResponseData((prev: any[]) =>
        prev.map((asset: any) => {
          if (asset.data_asset.toLowerCase() === editItem.originalName.toLowerCase()) {
            return {
              ...asset,
              data_asset: editedName,
              technology: selectedTechnologies, // empty allowed
            };
          }
          return asset;
        })
      );

      onClose();
    };

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '500px',
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Edit Data Source
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Name
            </Typography>
            <TextField
              variant="outlined"
              sx={{ width: 'auto' }}
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Source Type
            </Typography>
            <Select
              variant="outlined"
              sx={{ width: 'auto' }}
              value={editedSourceCategory}
              onChange={(e) => setEditedSourceCategory(e.target.value as 'internal' | 'external')}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value={'internal'}>Internal</MenuItem>
              <MenuItem value={'external'}>External</MenuItem>
            </Select>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Data Structure
            </Typography>
            <Select
              variant="outlined"
              sx={{ width: 'auto' }}
              value={editedDataStructure}
              onChange={(e) => setEditedDataStructure(e.target.value)}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value={'Structured'}>Structured</MenuItem>
              <MenuItem value={'Unstructured'}>Unstructured</MenuItem>
            </Select>
          </Box>
          {availableTechnologies.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" sx={{ fontSize: '14px' }}>
                Technologies (from AI Solution)
              </Typography>
              <Select
                multiple
                variant="outlined"
                sx={{ width: 'auto' }}
                value={selectedTechnologies}
                onChange={handleTechnologyChange}
                input={<OutlinedInput />}
                renderValue={(selected) => selected.join(', ')}
                MenuProps={{ disablePortal: true }}
              >
                {availableTechnologies.map((tech: string) => (
                  <MenuItem key={tech} value={tech}>
                    <Checkbox checked={selectedTechnologies.indexOf(tech) > -1} />
                    <ListItemText primary={tech} />
                  </MenuItem>
                ))}
              </Select>
              <Typography
                variant="caption"
                sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}
              >
                {selectedTechnologies.length > 0
                  ? `${selectedTechnologies.length} selected`
                  : 'No technologies selected'}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box
              onClick={handleDeleteDataRequirement}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': { opacity: 0.7 },
              }}
            >
              <img src="/images/DeleteRed.svg" height="16px" />
              <Typography variant="caption" sx={{ fontSize: '14px', color: 'black' }}>
                Delete
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleUpdateDataRequirement}
            >
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'agent_name') {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '500px',
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Edit Agent
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Enabler
            </Typography>
            <Select
              variant="outlined"
              sx={{ width: 'auto' }}
              id={`enabler-${editItem.name}`}
              value={
                solutionData.llm_agent.find(
                  (item: any, index: number) =>
                    item.agent_name === editItem.name || index === editItem.index
                )?.agent_enabler[0]
              }
              onChange={(e) => {
                setSolutionData({
                  ...solutionData,
                  llm_agent: solutionData.llm_agent.map((item: any, index: number) =>
                    item.agent_name === editItem.name || index === editItem.index
                      ? { ...item, agent_enabler: [e.target.value] }
                      : item
                  ),
                });
              }}
              MenuProps={{ disablePortal: true }}
            >
              {solutionData.llm_agent
                .find(
                  (item: any, index: number) =>
                    item.agent_name === editItem.name || index === editItem.index
                )
                ?.agent_enabler.map((enabler: any) => (
                  <MenuItem key={enabler} value={enabler}>
                    {enabler}
                  </MenuItem>
                ))}
            </Select>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Agent Name
            </Typography>
            <TextField
              variant="outlined"
              sx={{ width: 'auto' }}
              defaultValue={
                solutionData.llm_agent.find(
                  (item: any, index: number) =>
                    item.agent_name === editItem.name || index === editItem.index
                )?.agent_name
              }
              onChange={(e) => {
                setSolutionData({
                  ...solutionData,
                  llm_agent: solutionData.llm_agent.map((item: any, index: number) =>
                    item.agent_name === editItem.name || index === editItem.index
                      ? { ...item, agent_name: e.target.value }
                      : item
                  ),
                });
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Task
            </Typography>
            <TextareaAutosize
              aria-label="minimum height"
              minRows={3}
              defaultValue={
                solutionData.llm_agent.find((item: any) => item.agent_name === editItem.name)
                  ?.agent_description
              }
              onChange={(e) => {
                setSolutionData({
                  ...solutionData,
                  llm_agent: solutionData.llm_agent.map((item: any) =>
                    item.agent_name === editItem.name
                      ? { ...item, agent_description: e.target.value }
                      : item
                  ),
                });
              }}
              style={{
                width: 'auto',
                fontFamily: 'Poppins',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                padding: '8px',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                '&:hover': { cursor: 'pointer', backgroundColor: 'none' },
              }}
              onClick={() => {
                setSolutionData({
                  ...solutionData,
                  llm_agent: solutionData.llm_agent.filter(
                    (item: any, index: number) =>
                      item.agent_name !== editItem.name || index !== editItem.index
                  ),
                });
                onClose();
              }}
            >
              <img src="/images/DeleteRed.svg" height="16px" />
              <Typography variant="caption" sx={{ fontSize: '14px', color: 'black' }}>
                Delete
              </Typography>
            </Box>
            <Button variant="contained" size="small" color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'add_data_requirements') {
    // Find matching ai_solution entry based on the current data asset name
    const matchingAiSolution = solutionData?.agent?.ai_solution?.find(
      (solution: any) => solution.data_asset.toLowerCase() === editItem.originalName.toLowerCase()
    );

    // Get available technologies from the matching ai_solution
    const availableTechnologies = matchingAiSolution?.technology || [];

    const handleTechnologyChange = (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value;
      setSelectedTechnologies(typeof value === 'string' ? value.split(',') : value);
    };

    const handleAddDataRequirement = () => {
      // Validate input
      if (!editedName.trim()) {
        alert('Please enter a data source name');
        return;
      }

      const sourceCategory = editedSourceCategory;
      const structureType = editedDataStructure.toLowerCase() as 'structured' | 'unstructured';

      const updatedDataSources = { ...solutionData.data_sources };

      // Check for duplicates
      if (updatedDataSources[sourceCategory][structureType].includes(editedName.trim())) {
        alert('This data source already exists');
        return;
      }

      // Add to the appropriate array
      updatedDataSources[sourceCategory][structureType] = [
        ...updatedDataSources[sourceCategory][structureType],
        editedName.trim(),
      ];

      // If there's a matching ai_solution and technologies are selected, create or update the entry
      if (matchingAiSolution || selectedTechnologies.length > 0) {
        const existingAiSolutions = solutionData.agent?.ai_solution || [];
        let updatedAiSolution;

        if (matchingAiSolution) {
          // Update existing ai_solution entry
          updatedAiSolution = existingAiSolutions.map((solution: any) => {
            if (solution.data_asset.toLowerCase() === editItem.originalName.toLowerCase()) {
              return {
                ...solution,
                data_asset: editedName,
                technology: selectedTechnologies,
              };
            }
            return solution;
          });
        } else if (selectedTechnologies.length > 0) {
          // Create new ai_solution entry
          updatedAiSolution = [
            ...existingAiSolutions,
            { data_asset: editedName.trim(), technology: selectedTechnologies },
          ];
        } else {
          updatedAiSolution = existingAiSolutions;
        }

        const updatedSolution = {
          ...solutionData,
          data_sources: updatedDataSources,
          agent: {
            ...solutionData.agent,
            ai_solution: updatedAiSolution,
          },
        };
        setSolutionData(updatedSolution);
        onSave(updatedSolution);
      } else {
        const updatedSolution = { ...solutionData, data_sources: updatedDataSources };
        setSolutionData(updatedSolution);
        onSave(updatedSolution);
      }
      onClose();
    };

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '500px',
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Add Data Source
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Name
            </Typography>
            <TextField
              variant="outlined"
              sx={{ width: 'auto' }}
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              placeholder="Enter data source name"
              autoFocus
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Source Type
            </Typography>
            <Select
              variant="outlined"
              sx={{ width: 'auto' }}
              value={editedSourceCategory}
              onChange={(e) => setEditedSourceCategory(e.target.value as 'internal' | 'external')}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value={'internal'}>Internal</MenuItem>
              <MenuItem value={'external'}>External</MenuItem>
            </Select>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Data Structure
            </Typography>
            <Select
              variant="outlined"
              sx={{ width: 'auto' }}
              value={editedDataStructure}
              onChange={(e) => setEditedDataStructure(e.target.value)}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value={'Structured'}>Structured</MenuItem>
              <MenuItem value={'Unstructured'}>Unstructured</MenuItem>
            </Select>
          </Box>
          {availableTechnologies.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" sx={{ fontSize: '14px' }}>
                Technologies (from AI Solution)
              </Typography>
              <Select
                multiple
                variant="outlined"
                sx={{ width: 'auto' }}
                value={selectedTechnologies}
                onChange={handleTechnologyChange}
                input={<OutlinedInput />}
                renderValue={(selected) => selected.join(', ')}
                MenuProps={{ disablePortal: true }}
              >
                {availableTechnologies.map((tech: string) => (
                  <MenuItem key={tech} value={tech}>
                    <Checkbox checked={selectedTechnologies.indexOf(tech) > -1} />
                    <ListItemText primary={tech} />
                  </MenuItem>
                ))}
              </Select>
              <Typography
                variant="caption"
                sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}
              >
                {selectedTechnologies.length > 0
                  ? `${selectedTechnologies.length} selected`
                  : 'No technologies selected'}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleAddDataRequirement}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'add_key_requirements') {
    const handleAddKeyRequirement = () => {
      // Validate input
      if (!newRequirement.trim()) {
        alert('Please enter a key requirement');
        return;
      }

      // Check for duplicates
      if (solutionData.key_requirements?.includes(newRequirement.trim())) {
        alert('This requirement already exists');
        return;
      }

      // Add to key_requirements array
      const updatedKeyRequirements = [
        ...(solutionData.key_requirements || []),
        newRequirement.trim(),
      ];
      const updatedSolution = { ...solutionData, key_requirements: updatedKeyRequirements };
      setSolutionData(updatedSolution);
      onSave(updatedSolution);
      onClose();
    };

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '500px',
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Add Key Requirement
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ fontSize: '14px' }}>
              Requirement
            </Typography>
            <TextareaAutosize
              aria-label="minimum height"
              minRows={3}
              value={newRequirement}
              onChange={(e) => setNewRequirement(e.target.value)}
              placeholder="Enter key requirement description"
              style={{
                width: 'auto',
                fontFamily: 'Poppins',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                padding: '8px',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleAddKeyRequirement}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'embedded') {
    const embeddedList = solutionData.agent?.ai_enablement?.enablement_matrix?.embedded || [];

    const currentItem = embeddedList[editItem?.index];

    if (!currentItem) return null;

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box sx={{ width: 500, bgcolor: '#fff', p: 2, borderRadius: 1 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontWeight={600}>Edit Embedded Capability</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Name */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">Name</Typography>
            <TextField
              fullWidth
              value={currentItem.capability_name}
              onChange={(e) => {
                const updated = [...embeddedList];
                updated[editItem.index] = {
                  ...updated[editItem.index],
                  capability_name: e.target.value,
                };

                setSolutionData({
                  ...solutionData,
                  agent: {
                    ...solutionData.agent,
                    ai_enablement: {
                      ...solutionData.agent.ai_enablement,
                      enablement_matrix: {
                        ...solutionData.agent.ai_enablement.enablement_matrix,
                        embedded: updated,
                      },
                    },
                  },
                });
              }}
            />
          </Box>

          {/* Description */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">Description</Typography>
            <TextareaAutosize
              minRows={3}
              value={currentItem.description}
              onChange={(e) => {
                const updated = [...embeddedList];
                updated[editItem.index] = {
                  ...updated[editItem.index],
                  description: e.target.value,
                };

                setSolutionData({
                  ...solutionData,
                  agent: {
                    ...solutionData.agent,
                    ai_enablement: {
                      ...solutionData.agent.ai_enablement,
                      enablement_matrix: {
                        ...solutionData.agent.ai_enablement.enablement_matrix,
                        embedded: updated,
                      },
                    },
                  },
                });
              }}
              style={{
                width: '96%',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                padding: '10px',
                fontFamily: 'Poppins',
                fontSize: '14px',
              }}
            />
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                onSave(solutionData);
                onClose();
              }}
            >
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'extended') {
    const extendedList = solutionData.agent?.ai_enablement?.enablement_matrix?.extended || [];

    const currentItem = extendedList[editItem?.index];

    if (!currentItem) return null;

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box sx={{ width: 500, bgcolor: '#fff', p: 2, borderRadius: 1 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontWeight={600}>Edit Extended Capability</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Name */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">Name</Typography>
            <TextField
              fullWidth
              value={currentItem.capability_name}
              onChange={(e) => {
                const updated = [...extendedList];
                updated[editItem.index] = {
                  ...updated[editItem.index],
                  capability_name: e.target.value,
                };

                setSolutionData({
                  ...solutionData,
                  agent: {
                    ...solutionData.agent,
                    ai_enablement: {
                      ...solutionData.agent.ai_enablement,
                      enablement_matrix: {
                        ...solutionData.agent.ai_enablement.enablement_matrix,
                        extended: updated,
                      },
                    },
                  },
                });
              }}
            />
          </Box>

          {/* Description */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">Description</Typography>
            <TextareaAutosize
              minRows={3}
              value={currentItem.description}
              onChange={(e) => {
                const updated = [...extendedList];
                updated[editItem.index] = {
                  ...updated[editItem.index],
                  description: e.target.value,
                };

                setSolutionData({
                  ...solutionData,
                  agent: {
                    ...solutionData.agent,
                    ai_enablement: {
                      ...solutionData.agent.ai_enablement,
                      enablement_matrix: {
                        ...solutionData.agent.ai_enablement.enablement_matrix,
                        extended: updated,
                      },
                    },
                  },
                });
              }}
              style={{
                width: '96%',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                padding: '10px',
                fontFamily: 'Poppins',
                fontSize: '14px',
              }}
            />
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                onSave(solutionData);
                onClose();
              }}
            >
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'custom') {
    const customList = solutionData.agent?.ai_enablement?.enablement_matrix?.custom || [];

    const currentItem = customList[editItem?.index];

    if (!currentItem) return null;

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box sx={{ width: 500, bgcolor: '#fff', p: 2, borderRadius: 1 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontWeight={600}>Edit Custom Capability</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Name */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">Name</Typography>
            <TextField
              fullWidth
              value={currentItem.capability_name}
              onChange={(e) => {
                const updated = [...customList];
                updated[editItem.index] = {
                  ...updated[editItem.index],
                  capability_name: e.target.value,
                };

                setSolutionData({
                  ...solutionData,
                  agent: {
                    ...solutionData.agent,
                    ai_enablement: {
                      ...solutionData.agent.ai_enablement,
                      enablement_matrix: {
                        ...solutionData.agent.ai_enablement.enablement_matrix,
                        custom: updated,
                      },
                    },
                  },
                });
              }}
            />
          </Box>

          {/* Description */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">Description</Typography>
            <TextareaAutosize
              minRows={3}
              value={currentItem.description}
              onChange={(e) => {
                const updated = [...customList];
                updated[editItem.index] = {
                  ...updated[editItem.index],
                  description: e.target.value,
                };

                setSolutionData({
                  ...solutionData,
                  agent: {
                    ...solutionData.agent,
                    ai_enablement: {
                      ...solutionData.agent.ai_enablement,
                      enablement_matrix: {
                        ...solutionData.agent.ai_enablement.enablement_matrix,
                        custom: updated,
                      },
                    },
                  },
                });
              }}
              style={{
                width: '96%',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                padding: '10px',
                fontFamily: 'Poppins',
                fontSize: '14px',
              }}
            />
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                onSave(solutionData);
                onClose();
              }}
            >
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
  if (type === 'risk') {
    const riskList = solutionData?.agent?.ai_enablement?.risks_and_mitigation || [];

    const currentItem = riskList[editItem?.index];

    if (!currentItem) return null;

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box sx={{ width: 520, bgcolor: '#fff', p: 2, borderRadius: 1 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontWeight={600}>Edit Risk & Mitigation</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Risk Title */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">Title</Typography>
            <TextField
              fullWidth
              value={currentItem.risk_title}
              onChange={(e) => {
                const updated = [...riskList];
                updated[editItem.index] = {
                  ...updated[editItem.index],
                  risk_title: e.target.value,
                };

                setSolutionData({
                  ...solutionData,
                  agent: {
                    ...solutionData.agent,
                    ai_enablement: {
                      ...solutionData.agent.ai_enablement,
                      risks_and_mitigation: updated,
                    },
                  },
                });
              }}
            />
          </Box>

          {/* Risk Overview */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">Overview</Typography>
            <TextareaAutosize
              minRows={3}
              value={currentItem.risk_overview}
              onChange={(e) => {
                const updated = [...riskList];
                updated[editItem.index] = {
                  ...updated[editItem.index],
                  risk_overview: e.target.value,
                };

                setSolutionData({
                  ...solutionData,
                  agent: {
                    ...solutionData.agent,
                    ai_enablement: {
                      ...solutionData.agent.ai_enablement,
                      risks_and_mitigation: updated,
                    },
                  },
                });
              }}
              style={{
                width: '96%',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                padding: '10px',
                fontFamily: 'Poppins',
                fontSize: '14px',
              }}
            />
          </Box>

          {/* Mitigation */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">Mitigation</Typography>
            <TextareaAutosize
              minRows={3}
              value={currentItem.mitigation}
              onChange={(e) => {
                const updated = [...riskList];
                updated[editItem.index] = {
                  ...updated[editItem.index],
                  mitigation: e.target.value,
                };

                setSolutionData({
                  ...solutionData,
                  agent: {
                    ...solutionData.agent,
                    ai_enablement: {
                      ...solutionData.agent.ai_enablement,
                      risks_and_mitigation: updated,
                    },
                  },
                });
              }}
              style={{
                width: '96%',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                padding: '10px',
                fontFamily: 'Poppins',
                fontSize: '14px',
              }}
            />
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                onSave(solutionData);
                onClose();
              }}
            >
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
};

export default SolutionEditModal;
